import { Component, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { PublicOrganization } from '@se-po/shared-data-access-models'
import { OrganizationService } from '@se-po/shared-data-access-services'
import { environment } from '@se-po/shared-environments'
import { firstValueFrom } from 'rxjs'

const INBOX_ELEMENT_NAME = 'se-inbox-element'
const INBOX_SCRIPT_NAMES = ['runtime', 'polyfills', 'element']

@Component({
  selector: 'app-messages-new-page',
  templateUrl: './messages-new-page.component.html'
})
export class MessagesNewPageComponent implements OnInit {
  private static loadPromise: Promise<void>

  public messagingConfig: object
  public elementReady = false

  constructor(
    private activatedRoute: ActivatedRoute,
    private organizationService: OrganizationService
  ) {}

  public async ngOnInit(): Promise<void> {
    const orgs = await this.userOrgs()
    if (orgs.length > 0) {
      this.messagingConfig = this.buildMessagingConfig(orgs)
    }

    await this.ensureElementDefined()
    this.elementReady = true
  }

  private async userOrgs(): Promise<PublicOrganization[]> {
    const queryOrgId = this.activatedRoute.snapshot.queryParams['orgId']
    if (queryOrgId) {
      try {
        const org = await firstValueFrom(this.organizationService.findPublic(queryOrgId.toString()))
        return org ? [org] : []
      } catch {
        return []
      }
    }
    try {
      const response = await firstValueFrom(this.organizationService.myAdminAccess())
      const adminOrgs = response.result ?? []
      if (adminOrgs.length > 0) return adminOrgs
    } catch {
      // fall through to mine()
    }
    try {
      const response = await firstValueFrom(this.organizationService.mine())
      return response.result ?? []
    } catch {
      return []
    }
  }

  private buildMessagingConfig(orgs: PublicOrganization[]): object {
    const primary = orgs[0]
    const recipientGroups = orgs.flatMap(org => [
      {
        resourceSystem: 'TeamService',
        resourceType: 'Team',
        params: { org_id: org.id, per_page: 100 }
      },
      {
        resourceSystem: 'UserService',
        resourceType: 'Persona',
        params: { org_id: org.id, exclude_teams: 'true', per_page: 100 }
      },
      {
        resourceSystem: 'Ngin',
        resourceType: 'Group',
        params: { org_id: org.id, per_page: 100, type: 'active' }
      }
    ])
    return {
      orgId: primary.id.toString(),
      orgName: primary.name,
      orgEmail: primary.email,
      recipientGroups
    }
  }

  private async ensureElementDefined(): Promise<void> {
    if (customElements.get(INBOX_ELEMENT_NAME)) return
    MessagesNewPageComponent.loadPromise = MessagesNewPageComponent.loadPromise || this.loadInboxElement()
    await MessagesNewPageComponent.loadPromise
    await customElements.whenDefined(INBOX_ELEMENT_NAME)
  }

  private async loadInboxElement(): Promise<void> {
    for (const scriptName of INBOX_SCRIPT_NAMES) {
      await this.loadScript(`${environment.baseUrl}/se-inbox/${scriptName}.js`)
    }
  }

  private loadScript(src: string): Promise<void> {
    if (document.querySelector(`script[src="${src}"]`)) return Promise.resolve()

    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.async = false
      script.src = src
      script.type = 'module'
      script.onload = () => resolve()
      script.onerror = () => reject()
      document.body.appendChild(script)
    })
  }
}
